"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Phone, Mail, MapPin, ArrowLeft, MessageCircle, Map, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardFooter, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    const rect = node.getBoundingClientRect();
    const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    if (alreadyVisible) {
      setIsVisible(true);
    }
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (!node || once && isVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold, isVisible]);
  return {
    ref,
    isVisible
  };
}
const contactMethods = [{
  id: "phone",
  title: "Línea Directa",
  value: "+1 (540) 421-5105",
  description: "Hablemos sobre tu próximo proyecto musical o evento especial.",
  icon: Phone,
  ctaIcon: MessageCircle,
  ctaText: "Llamar ahora",
  ctaLink: "tel:+18007658376",
  isExternal: false
}, {
  id: "email",
  title: "Correo Creativo",
  value: "info@rolvermusicproductions.com",
  description: "Envíanos tus maquetas o ideas para una cotización personalizada.",
  icon: Mail,
  ctaIcon: Send,
  ctaText: "Enviar correo",
  ctaLink: "mailto:info@rolvermusicproductions.com",
  isExternal: false
}, {
  id: "address",
  title: "Nuestro Estudio",
  value: "Sede Central de Producción",
  description: "Donde la fe y la creatividad se encuentran para crear arte.",
  icon: MapPin,
  ctaIcon: Map,
  ctaText: "Ver ubicación",
  ctaLink: "https://maps.google.com/?q=Music+Production+Studio",
  isExternal: true
}];
export default function HablemosDeTuMSica() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="265180" id="hablemos-de-tu-música" className="py-24 bg-background text-foreground relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-lg bg-primary/10 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-lg bg-secondary/10 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground mb-6 tracking-tight">
            Hablemos de tu <span className="text-primary">Música&nbsp;</span>
          </h2>
          <p className="text-lg text-muted-foreground font-default leading-relaxed">
            En Rolver Music Productions, creemos que cada historia merece ser contada a través de la música. Comparte tu visión con nosotros y te ayudaremos a convertirla en una producción musical significativa que refleje tu mensaje y llegue al corazón de quienes la escuchan.
          </p>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {contactMethods.map((method, i) => {
          const Icon = method.icon;
          const CtaIcon = method.ctaIcon;
          return <div key={method.id} data-index={i} className={`transition-all duration-700 ease-out delay-${i * 100} ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: `${i * 150}ms`
          }}>
                <Card className="relative h-full overflow-hidden border-border/40 bg-card/40 backdrop-blur-xl shadow-xl hover:shadow-2xl hover:border-primary/50 transition-all duration-500 group flex flex-col">
                  {}
                  <Icon className="absolute -bottom-8 -left-8 size-48 text-primary/5 -rotate-12 group-hover:text-primary/10 group-hover:scale-110 transition-all duration-500 pointer-events-none" />
                  
                  <CardHeader className="pb-4 relative z-10">
                    <div className="size-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-500 shadow-sm">
                      <Icon className="size-7 text-primary group-hover:text-primary-foreground transition-colors duration-500" />
                    </div>
                    <CardTitle className="text-xl font-bold font-default text-foreground mb-2">
                      {method.title}
                    </CardTitle>
                    <div className={`${method.id === 'email' ? 'text-base md:text-lg' : 'text-2xl md:text-3xl'} font-bold font-default text-foreground tracking-tight mb-2`}>
                      {method.value}
                    </div>
                  </CardHeader>
                  
                  <CardContent className="relative z-10 flex-grow">
                    <CardDescription className="text-base text-muted-foreground font-default">
                      {method.description}
                    </CardDescription>
                  </CardContent>
                  
                  <CardFooter className="pt-6 relative z-10 mt-auto">
                    <Button asChild variant="outline" className="w-full group/btn border-border/50 hover:border-primary/50 hover:bg-primary/10 rounded-md transition-all duration-300">
                      <Link to={method.ctaLink} newTab={method.isExternal}>
                        <CtaIcon className="size-4 mr-2 text-primary group-hover/btn:scale-110 transition-transform" />
                        <span className="font-default font-semibold">{method.ctaText}</span>
                        <ArrowLeft className="size-4 ml-auto opacity-0 translate-x-2 group-hover/btn:opacity-100 group-hover/btn:translate-x-0 transition-all duration-300 text-primary rotate-180" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}